import React from "react";
import { X } from "lucide-react";

/**
 * Generic confirmation modal.
 *
 * Props:
 *   title       — modal heading
 *   message     — body text (string or JSX)
 *   confirmLabel — text for the confirm button (default "Confirm")
 *   confirmVariant — "danger" | "primary" (default "danger")
 *   onConfirm   — called when confirm is clicked
 *   onCancel    — called when cancel or backdrop is clicked
 */
export default function ConfirmModal({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "danger",
  onConfirm,
  onCancel,
}) {
  const confirmCls =
    confirmVariant === "danger"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : "bg-zinc-950 hover:bg-zinc-800 text-white";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h2 className="text-sm font-bold text-zinc-950">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="h-6 w-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-950 hover:bg-zinc-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className="text-sm text-zinc-600 leading-relaxed">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-100">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 px-4 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`h-9 px-4 rounded-md text-sm font-semibold ${confirmCls}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
